import dayjs from "dayjs";
/**
 * Check if a value is a DateString.
 *
 * This uses regex instead of date parsing, so it will not check if the date is valid, only the format.
 * See: isValidDateString for a more strict check.
 */
export function isDateString(value) {
    if (typeof value !== "string") {
        return false;
    }
    // Regex for YYYY-MM-DD format
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(value)) {
        return false;
    }
    return true;
}
/**
 * Check if a value is a valid date string.
 */
export function isValidDateString(value) {
    if (typeof value !== "string") {
        return false;
    }
    // Regex for YYYY-MM-DD format
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(value)) {
        return false;
    }
    // Check if the date is valid using dayjs
    const parsed = dayjs(value);
    return parsed.isValid() && parsed.format("YYYY-MM-DD") === value; // Dayjs will happily roll invalid dates over
}
/**
 * Convert a string to a DateString if possible, throwing if the string can't be converted.
 * @param value The string to convert
 * @param stripTime If true, the time will be stripped from the string, and only the date portion will be considered.
 */
export function toDateString(value, stripTime = false) {
    if (stripTime) {
        value = value.split("T")[0]; // Strip time from the string
    }
    // Try to parse with dayjs and check if it's a valid date
    const parsed = dayjs(value);
    if (parsed.isValid()) {
        return parsed.format("YYYY-MM-DD");
    }
    throw new Error(`Invalid date string format or value: ${value}. Expected YYYY-MM-DD.`);
}
/**
 * Convert a string to a DateString if possible, returning undefined if the string can't be converted.
 */
export function toDateStringOrUndefined(value, stripTime = false) {
    if (value === undefined || value === null) {
        return undefined;
    }
    return toDateString(value, stripTime);
}
/**
 * Get the current date in YYYY-MM-DD format
 */
export function getCurrentDateString() {
    return dayjs().format("YYYY-MM-DD");
}
/**
 * Convert a Date object to a DateString.
 * @param date The Date object to convert
 */
export function dateToDateString(date) {
    return dayjs(date).format("YYYY-MM-DD");
}
/**
 * Parses a date string of unknown format into a DateString in YYYY-MM-DD format.
 * @param s Date string of unknown format (e.g., "10/1/2023", "10/1/23", "October 1, 2023", etc.)
 * @returns DateString in our standard YYYY-MM-DD format or null if invalid
 */
export function parseDateFromExcelDate(s) {
    if (!s?.trim())
        return null;
    for (const fmt of [
        "YYYY-MM-DD",
        "M/D/YYYY",
        "MM/DD/YYYY",
        "M/D/YY",
        "MM/DD/YY",
    ]) {
        const d = dayjs(s.trim(), fmt, true);
        if (d.isValid())
            return d.format("YYYY-MM-DD");
    }
    const d = dayjs(s.trim());
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
}
